import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { useLocaleStore } from '@/features/locale/store/locale-store';
import { useLocationStore } from '@/features/location/store/location-store';
import { locationInfoApi } from '../api/location-info-api';
import { apiClient } from '@/lib/api-client';

interface GeocodingResult {
  countryCode?: string;
  countryName?: string;
  formattedAddress?: string;
}

/**
 * Reverse geocode coordinates to get country code and formatted address using Monta's geocoding API
 * Uses the authenticated Monta API endpoint /geocoder/reverse
 * Returns country code and formatted address from the first result
 */
async function getLocationFromCoordinates(
  lat: number,
  lng: number
): Promise<GeocodingResult> {
  try {
    // Use Monta's own geocoding API endpoint
    // Response format: Array of location results with nested country object
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    const response = await apiClient.get<any>(
      `/api/v1/geocoder/reverse?lat=${lat}&lng=${lng}`
    );

    // Monta API returns an array of results
    if (Array.isArray(response) && response.length > 0) {
      // Get first result's country.code_2 and formatted_address
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const firstResult = response[0];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const countryCode = firstResult?.country?.code_2;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const countryName = firstResult?.country?.name;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const formattedAddress = firstResult?.formatted_address;

      return {
        countryCode:
          countryCode && typeof countryCode === 'string'
            ? countryCode.toUpperCase()
            : undefined,
        countryName:
          countryName && typeof countryName === 'string'
            ? countryName
            : undefined,
        formattedAddress:
          formattedAddress && typeof formattedAddress === 'string'
            ? formattedAddress
            : undefined,
      };
    }

    console.warn('No location data found in Monta geocoding response');
    return {};
  } catch (error) {
    console.error('Failed to reverse geocode location with Monta API:', error);
    return {};
  }
}

/**
 * Hook to get location information (currency, operators) from cached data
 * Filters operators based on country detected from current map center
 *
 * Uses Monta's own /geocoder/reverse API to determine country from map center coordinates
 */
export function useLocationInformation(options?: { enabled?: boolean }) {
  // Get all cached operators from locale store
  const allOperators = useLocaleStore((s) => s.allOperators);
  const isOperatorsCacheInitialized = useLocaleStore(
    (s) => s.isOperatorsCacheInitialized
  );

  // Get current map center
  const mapCenter = useLocationStore((s) => s.mapCenter);

  // Debounce map center by 2 seconds - same pattern as charge points map API
  // This prevents excessive API calls while user is actively panning/zooming
  const [debouncedMapCenter] = useDebounce(mapCenter, 2000);

  // Round coordinates to 2 decimal places (~1km precision) to reduce API calls
  // Only refetch when moving significantly (e.g., different region)
  // This provides a good balance between accuracy and reducing unnecessary API calls
  const roundedCenter = debouncedMapCenter
    ? {
        lat: Math.round(debouncedMapCenter.lat * 100) / 100,
        lng: Math.round(debouncedMapCenter.lng * 100) / 100,
      }
    : null;

  return useQuery({
    queryKey: ['location-information-cached', roundedCenter],
    queryFn: async () => {
      // Get fresh map center from store inside query function
      const currentMapCenter = useLocationStore.getState().mapCenter;

      if (!roundedCenter || !currentMapCenter) {
        return locationInfoApi.getLocationInfoFromCache(
          undefined,
          allOperators
        );
      }

      // Reverse geocode to get country code and address using full precision map center
      // Use full precision for accurate country detection, but cache based on rounded coords
      const locationData = await getLocationFromCoordinates(
        currentMapCenter.lat,
        currentMapCenter.lng
      );

      // Filter cached operators by country and add location metadata
      const locationInfo = locationInfoApi.getLocationInfoFromCache(
        locationData.countryCode,
        allOperators
      );

      // Prefer the geocode's country for display. The operator cache may not
      // have an entry for this country (e.g. France with no cached operators),
      // but the reverse-geocode response always includes the country name +
      // code — so the card shows the flag + name instead of "Unknown".
      return {
        ...locationInfo,
        countryName: locationData.countryName ?? locationInfo.countryName,
        countryCode: locationData.countryCode ?? locationInfo.countryCode,
        formattedAddress: locationData.formattedAddress,
        coordinates: {
          lat: currentMapCenter.lat,
          lng: currentMapCenter.lng,
        },
      };
    },
    enabled:
      options?.enabled !== false &&
      isOperatorsCacheInitialized &&
      allOperators.length > 0 &&
      !!roundedCenter,
    // Keep the previous location visible while a pan-triggered refetch runs,
    // so the card doesn't flash "Loading..." and jump on every map move.
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
